import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, PhoneOff, Mic, AlertCircle, Clock, RefreshCw } from '../icons';
import { getReframingVoiceToken } from '../../lib/reframing-practice';
import type { ReframingPracticeMode, ReframingDifficulty, ReframingContext, ReframingFeedbackTiming } from './reframingPracticeConstants';

const MAX_SESSION_SECONDS = 20 * 60;
const WARNING_SECONDS = 19 * 60;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  practice_mode: ReframingPracticeMode;
  difficulty: ReframingDifficulty;
  context: ReframingContext;
  feedback_timing: ReframingFeedbackTiming;
  session_length_minutes?: number;
  onSessionEnd: (durationSeconds: number, sessionId: string | null) => void;
}

type SessionStatus = 'idle' | 'connecting' | 'connected' | 'ended' | 'error';

interface TranscriptEntry {
  role: 'user' | 'assistant';
  text: string;
  timestamp: number;
}

export function ReframingPracticeVoice({
  isOpen, onClose, practice_mode, difficulty, context, feedback_timing,
  session_length_minutes, onSessionEnd,
}: Props) {
  const [status, setStatus] = useState<SessionStatus>('idle');
  const [duration, setDuration] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [practiceSessionId, setPracticeSessionId] = useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const transcriptEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => () => { cleanup(); }, []);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    dcRef.current?.close();
    pcRef.current?.close();
    mediaStreamRef.current?.getTracks().forEach(t => t.stop());
    if (audioRef.current) { audioRef.current.srcObject = null; }
    timerRef.current = null;
    pcRef.current = null;
    dcRef.current = null;
    mediaStreamRef.current = null;
  }, []);

  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setDuration(elapsed);
      if (elapsed >= WARNING_SECONDS && !showWarning) setShowWarning(true);
      if (elapsed >= MAX_SESSION_SECONDS) handleEnd('time_limit');
    }, 1000);
  }, [showWarning]);

  const handleEnd = useCallback((reason?: string) => {
    const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
    if (reason !== 'user') {
      dcRef.current?.send(JSON.stringify({ type: 'session.update', session: { instructions: 'Please wrap up the session now.' } }));
    }
    cleanup();
    setStatus('ended');
    onSessionEnd(elapsed, practiceSessionId);
  }, [cleanup, onSessionEnd, practiceSessionId]);

  async function startSession() {
    setStatus('connecting');
    setErrorMsg('');
    setTranscript([]);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const { client_secret, practice_session_id } = await getReframingVoiceToken({
        practice_mode, difficulty, context, feedback_timing,
        session_length_minutes: session_length_minutes || 10,
        interaction_mode: 'voice',
      });
      setPracticeSessionId(practice_session_id);

      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.autoplay = true;
      }

      pc.ontrack = (e) => {
        if (audioRef.current) audioRef.current.srcObject = e.streams[0];
      };

      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;

      dc.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);
          if (event.type === 'response.audio_transcript.delta' && event.delta) {
            setTranscript(prev => {
              const last = prev[prev.length - 1];
              if (last?.role === 'assistant') {
                return [...prev.slice(0, -1), { ...last, text: last.text + event.delta }];
              }
              return [...prev, { role: 'assistant', text: event.delta, timestamp: Date.now() }];
            });
          }
          if (event.type === 'conversation.item.input_audio_transcription.completed' && event.transcript) {
            setTranscript(prev => [...prev, { role: 'user', text: event.transcript, timestamp: Date.now() }]);
          }
        } catch { /* ignore parse errors */ }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpRes = await fetch('https://api.openai.com/v1/realtime/calls', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${client_secret}`,
          'Content-Type': 'application/sdp',
        },
        body: offer.sdp,
      });

      if (!sdpRes.ok) {
        const errBody = await sdpRes.text().catch(() => '');
        throw new Error(`WebRTC negotiation failed (${sdpRes.status}): ${errBody || 'Unknown error'}`);
      }

      const answerSdp = await sdpRes.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          handleEnd('disconnected');
        }
      };

      setStatus('connected');
      startTimer();
    } catch (err) {
      setErrorMsg((err as Error).message);
      setStatus('error');
      cleanup();
    }
  }

  function toggleMute() {
    mediaStreamRef.current?.getTracks().forEach(t => {
      if (t.kind === 'audio') t.enabled = muted;
    });
    setMuted(m => !m);
  }

  function sendCommand(command: string) {
    if (!dcRef.current || dcRef.current.readyState !== 'open') return;
    const messages: Record<string, object> = {
      repeat: { type: 'response.create', response: { instructions: 'Please repeat the last question or exercise exactly as you stated it.' } },
      hint: { type: 'response.create', response: { instructions: 'Please provide a helpful hint for the current exercise without giving away the answer.' } },
      feedback: { type: 'response.create', response: { instructions: 'Please provide detailed feedback on my last response using the scoring rubric.' } },
      pause: { type: 'response.create', response: { instructions: 'Please pause the role-play and step out of character briefly to check in with me.' } },
    };
    const msg = messages[command];
    if (msg) dcRef.current.send(JSON.stringify(msg));
  }

  const formatTime = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-700 to-teal-600 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-white font-semibold text-lg">Reframing AI Coach — Voice</p>
            <p className="text-teal-200 text-sm capitalize">{practice_mode.replace(/_/g, ' ')} · {difficulty} · {context}</p>
          </div>
          <div className="flex items-center gap-3">
            {status === 'connected' && (
              <span className="flex items-center gap-1.5 text-white text-sm font-mono">
                <Clock className="w-4 h-4" />
                {formatTime(duration)}
              </span>
            )}
          </div>
        </div>

        {/* Warning banner */}
        <AnimatePresence>
          {showWarning && (
            <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
              className="bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-center gap-2 text-amber-800 text-sm overflow-hidden">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              Session will end in less than a minute.
            </motion.div>
          )}
        </AnimatePresence>

        {/* Transcript */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 min-h-0">
          {transcript.length === 0 && status !== 'connected' && (
            <p className="text-gray-400 text-sm text-center py-8">Your conversation will appear here during the session.</p>
          )}
          {transcript.map((entry, i) => (
            <div key={i} className={`flex ${entry.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                entry.role === 'user'
                  ? 'bg-teal-600 text-white rounded-br-sm'
                  : 'bg-gray-100 text-gray-800 rounded-bl-sm'
              }`}>
                <p className={`text-xs font-semibold mb-1 ${entry.role === 'user' ? 'text-teal-100' : 'text-teal-700'}`}>
                  {entry.role === 'user' ? 'You' : 'Reframing Coach'}
                </p>
                {entry.text}
              </div>
            </div>
          ))}
          <div ref={transcriptEndRef} />
        </div>

        {/* Controls */}
        <div className="border-t border-gray-100 px-6 py-4">
          {status === 'idle' && (
            <button onClick={startSession}
              className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors">
              <Phone className="w-5 h-5" />
              Start Voice Session
            </button>
          )}

          {status === 'connecting' && (
            <div className="flex items-center justify-center gap-2 text-gray-500 py-3">
              <div className="w-4 h-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
              Connecting…
            </div>
          )}

          {status === 'connected' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <button onClick={toggleMute}
                  className={`flex flex-col items-center gap-1 py-2.5 rounded-xl text-xs font-medium transition-colors ${muted ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}>
                  {muted ? <Phone className="w-4 h-4 opacity-50" /> : <Mic className="w-4 h-4" />}
                  {muted ? 'Unmute' : 'Mute'}
                </button>
                <button onClick={() => sendCommand('repeat')}
                  className="flex flex-col items-center gap-1 py-2.5 rounded-xl text-xs font-medium bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors">
                  <RefreshCw className="w-4 h-4" />
                  Repeat
                </button>
                <button onClick={() => sendCommand('hint')}
                  className="flex flex-col items-center gap-1 py-2.5 rounded-xl text-xs font-medium bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors">
                  <AlertCircle className="w-4 h-4" />
                  Hint
                </button>
                <button onClick={() => sendCommand('feedback')}
                  className="flex flex-col items-center gap-1 py-2.5 rounded-xl text-xs font-medium bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors">
                  <Mic className="w-4 h-4" />
                  Feedback
                </button>
              </div>
              <button onClick={() => handleEnd('user')}
                className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors">
                <PhoneOff className="w-5 h-5" />
                End Session
              </button>
            </div>
          )}

          {status === 'ended' && (
            <div className="text-center py-2">
              <p className="text-gray-600 text-sm">Session ended. Duration: {formatTime(duration)}</p>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 text-red-600 text-sm bg-red-50 rounded-xl p-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>{errorMsg}</p>
              </div>
              <button onClick={startSession}
                className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold transition-colors">
                Try Again
              </button>
            </div>
          )}

          <button onClick={onClose} className="mt-2 w-full py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors">
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
}
